/**
 * Staff Nutrition Index — Phase 2.1B
 *
 * Bound to: GET /api/nutrition-analytics/staff-index?team_id=...
 *
 * Lists every athlete in the user's accessible teams with their core
 * nutrition KPIs (latest weight, BF%, lean mass, BMR, TDEE, active target,
 * window-Δ flags). Click row → AthleteNutritionDashboard.
 *
 * Role-aware: NUTRITIONIST + ADMIN see write-action CTAs, others see read-only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Apple, AlertTriangle, ArrowRight, Loader2, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const fmt = (v, dp = 1, suffix = '') =>
  v === null || v === undefined ? '—' : `${Number(v).toFixed(dp)}${suffix}`;
const fmtInt = (v, suffix = '') =>
  v === null || v === undefined ? '—' : `${Math.round(v)}${suffix}`;

const StaffNutritionIndexPage = () => {
  const { api, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamFilter, setTeamFilter] = useState('');

  const canWrite = ['ADMIN', 'NUTRITIONIST', 'SUPERADMIN'].includes(user?.role);

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await api.get('/teams');
        setTeams(res.data || []);
      } catch {
        setTeams([]);
      }
    };
    fetchTeams();
  }, [api]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = teamFilter ? `?team_id=${teamFilter}` : '';
        const res = await api.get(`/nutrition-analytics/staff-index${params}`);
        setData(res.data);
      } catch (e) {
        setData({ athletes: [] });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api, teamFilter]);

  const athletes = data?.athletes || [];

  const summary = useMemo(() => {
    const withAnthro = athletes.filter((a) => a.latest_weight_kg !== null && a.latest_weight_kg !== undefined);
    const withRisk = athletes.filter((a) =>
      (a.weight_change_window_pct !== null && Math.abs(a.weight_change_window_pct) >= 3) ||
      (a.lean_mass_change_window_pct !== null && a.lean_mass_change_window_pct <= -5),
    );
    const avgWeight = withAnthro.length
      ? withAnthro.reduce((s, a) => s + a.latest_weight_kg, 0) / withAnthro.length
      : null;
    return {
      total: athletes.length,
      withAnthro: withAnthro.length,
      flagged: withRisk.length,
      avgWeight,
    };
  }, [athletes]);

  return (
    <div className="space-y-6 p-6" data-testid="staff-nutrition-index">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Apple className="w-6 h-6 text-emerald-400" />
            <h1 className="text-3xl font-bold text-zinc-100">
              {language === 'IT' ? 'Nutrizione' : 'Nutrition'}
            </h1>
          </div>
          <p className="text-sm text-zinc-400">
            {language === 'IT'
              ? 'Panoramica nutrizionale per atleti delle tue squadre.'
              : 'Nutrition snapshot for athletes in your assigned teams.'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={teamFilter || 'all'} onValueChange={(v) => setTeamFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-56 bg-zinc-900 border-zinc-800" data-testid="team-filter">
              <SelectValue placeholder={language === 'IT' ? 'Tutte le squadre' : 'All teams'} />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="all">{language === 'IT' ? 'Tutte le squadre' : 'All teams'}</SelectItem>
              {teams.map((tm) => (
                <SelectItem key={tm.id} value={tm.id}>
                  {tm.code ? `${tm.code} — ${tm.name}` : tm.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {teamFilter && (
            <Button
              onClick={() => navigate(`/staff/nutrition/teams/${teamFilter}`)}
              data-testid="open-team-dashboard"
            >
              {language === 'IT' ? 'Dashboard Squadra' : 'Team Dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
          {canWrite && (
            <Button
              variant="outline"
              onClick={() => navigate('/staff/nutrition/executive')}
              data-testid="open-executive-dashboard"
            >
              {language === 'IT' ? 'Suite Executive' : 'Executive'}
            </Button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="kpi-total-athletes">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {language === 'IT' ? 'Atleti Totali' : 'Total Athletes'}
          </div>
          <div className="text-3xl font-bold text-cyan-300">{summary.total}</div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="kpi-with-anthro">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {language === 'IT' ? 'Con misurazioni' : 'With measurements'}
          </div>
          <div className="text-3xl font-bold text-emerald-400">{summary.withAnthro}</div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="kpi-flagged">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {language === 'IT' ? 'Atleti a rischio' : 'Risk athletes'}
          </div>
          <div className="text-3xl font-bold text-amber-400">{summary.flagged}</div>
        </Card>
        <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="kpi-avg-weight">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
            {language === 'IT' ? 'Peso medio' : 'Avg weight'}
          </div>
          <div className="text-3xl font-bold text-zinc-100">{fmt(summary.avgWeight, 1, ' kg')}</div>
        </Card>
      </div>

      <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="athletes-table">
        {loading ? (
          <div className="p-12 flex items-center justify-center text-zinc-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {language === 'IT' ? 'Caricamento…' : 'Loading…'}
          </div>
        ) : athletes.length === 0 ? (
          <div className="p-12 text-center text-zinc-500" data-testid="empty-state">
            <Users className="w-10 h-10 mx-auto mb-3 text-zinc-700" />
            <p>{language === 'IT' ? 'Nessun atleta trovato.' : 'No athletes found.'}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-zinc-950 text-zinc-400 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">{language === 'IT' ? 'Atleta' : 'Athlete'}</th>
                  <th className="px-4 py-3 text-left">{language === 'IT' ? 'Posizione' : 'Position'}</th>
                  <th className="px-4 py-3 text-right">{language === 'IT' ? 'Peso (kg)' : 'Weight (kg)'}</th>
                  <th className="px-4 py-3 text-right">BF %</th>
                  <th className="px-4 py-3 text-right">{language === 'IT' ? 'Magra (kg)' : 'Lean (kg)'}</th>
                  <th className="px-4 py-3 text-right">BMR</th>
                  <th className="px-4 py-3 text-right">TDEE</th>
                  <th className="px-4 py-3 text-right">{language === 'IT' ? 'Target' : 'Target'}</th>
                  <th className="px-4 py-3 text-center">Δ 28d</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {athletes.map((a) => {
                  const wPct = a.weight_change_window_pct;
                  const flagged = (wPct !== null && Math.abs(wPct) >= 3) ||
                    (a.lean_mass_change_window_pct !== null && a.lean_mass_change_window_pct <= -5);
                  return (
                    <tr
                      key={a.athlete_id}
                      className="hover:bg-zinc-800/60 cursor-pointer transition-colors"
                      onClick={() => navigate(`/staff/nutrition/athletes/${a.athlete_id}`)}
                      data-testid={`athlete-row-${a.athlete_id}`}
                    >
                      <td className="px-4 py-3 text-zinc-100">{a.first_name} {a.last_name}</td>
                      <td className="px-4 py-3 text-zinc-400">{a.primary_position || '—'}</td>
                      <td className="px-4 py-3 text-right text-zinc-200">{fmt(a.latest_weight_kg)}</td>
                      <td className="px-4 py-3 text-right text-zinc-200">{fmt(a.latest_bf_pct)}</td>
                      <td className="px-4 py-3 text-right text-zinc-200">{fmt(a.latest_lean_mass_kg)}</td>
                      <td className="px-4 py-3 text-right text-zinc-200">{fmtInt(a.latest_bmr_kcal)}</td>
                      <td className="px-4 py-3 text-right text-zinc-200">{fmtInt(a.latest_tdee_kcal)}</td>
                      <td className="px-4 py-3 text-right">
                        {a.active_target_kcal ? (
                          <span className="text-emerald-400">{fmtInt(a.active_target_kcal, ' kcal')}</span>
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {wPct === null || wPct === undefined ? (
                          <span className="text-zinc-600">—</span>
                        ) : (
                          <Badge
                            variant="outline"
                            className={
                              Math.abs(wPct) >= 3 ? 'border-amber-500/50 text-amber-300' : 'border-zinc-700 text-zinc-400'
                            }
                          >
                            {wPct > 0 ? '+' : ''}{wPct.toFixed(1)}%
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {flagged && <AlertTriangle className="w-4 h-4 inline text-amber-400" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default StaffNutritionIndexPage;
